import { useState } from 'react'
import { isEqual } from 'lodash'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import { translateSKPPeriod } from 'app/KinerjaPage/utils/kinerja'
import {
  apiPaths,
  useAPIUpdatePengembanganKompetensi,
} from 'api/kinerja/useAPIKinerja'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import DialogConfirmSaveEdit from '../components/DialogConfirmSaveEdit'
import TabPengembanganKompetensi from '../../Perencanaan/TabPengembanganKompetensi'

import type { Kinerja, KinerjaSelection } from 'types/kinerja'

interface EditPengembanganKompetensiProps {
  skp: Kinerja
  isSKPFetched: boolean
}

export default function EditPengembanganKompetensi({
  skp,
  isSKPFetched,
}: EditPengembanganKompetensiProps) {
  const { mutateAsync } = useAPIUpdatePengembanganKompetensi()
  const queryKeys = useGetKinerjaQuerykeys(apiPaths.kinerja())

  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()

  const handleClickBack = () => {
    router.back()
  }

  const [values, setValues] = useState<KinerjaSelection[]>(
    skp.reviewPengembanganKompetensi
  )
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleSave = async () => {
    try {
      await mutateAsync({
        pengembanganKompetensi: values,
      })

      await queryClient.invalidateQueries({
        queryKey: queryKeys,
      })

      toast({
        message: 'Berhasil simpan Rencana Hasil Kerja',
        type: 'success',
      })

      router.push(
        `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi`
      )
    } catch (e) {
      console.error(e)
      toast({
        message: 'Gagal simpan Rencana Hasil Kerja',
      })
    }
  }

  const isChanged = !isEqual(values, skp.reviewPengembanganKompetensi)

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          {/*
           * Note: we need to explicitly set font-size and line-height as workaround of styles collision
           * due to multiple version of @wartek-id/icon being used in this project.
           */}
          <Icon
            onClick={handleClickBack}
            className="text-black !text-4xl !leading-9"
            data-testid="icon-back"
            fontSize="lg"
          >
            arrow_back
          </Icon>
          <Text variant="display-md">Ubah Rencana Hasil Kerja</Text>
        </div>

        <PanelSummary
          isLoading={!isSKPFetched}
          items={[
            {
              label: translateSKPPeriod(skp.period),
              value: skp.tahun,
            },
          ]}
        />
      </div>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8 mb-[78px]">
          <TabPengembanganKompetensi
            skp={skp}
            isSubmitting={false}
            editReviewData={true}
            reviewMode={true}
            onChange={(v) => setValues(v)}
            adjustmentInfoText="Diubah oleh Anda"
            showCheckButton={false}
            allowDeleteSingleValue={false}
            customStickyFooter={() => {
              return (
                <Button
                  data-testid="btn-save"
                  size="lg"
                  disabled={!isChanged || values.length === 0}
                  onClick={() => {
                    setShowConfirmDialog(true)
                  }}
                >
                  Simpan
                </Button>
              )
            }}
          />
        </div>
      )}

      <DialogConfirmSaveEdit
        customSize={595}
        data-testid="dialog-confirm-save-edit"
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleSave}
      />
    </div>
  )
}
